import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, catchError, map } from 'rxjs/operators';
import { BaseServicesService } from './base-services.service';
import { LoggingService } from '../consoleLogs';

interface UserPermission {
  featureId: string;
  featureKey: string;
  permissionKeys: string[];
}

interface PermissionResponse {
  userId: string;
  permissions: UserPermission[];
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private permissions: UserPermission[] = [];
  private permissions$ = new BehaviorSubject<UserPermission[]>([]);

  constructor(private baseService: BaseServicesService, private loggingService : LoggingService) {}

  /** 🟢 Load permissions from backend (Lambda via BaseService) */
  loadPermissions(): Observable<PermissionResponse | null> {
    return this.baseService.getUserPermission().pipe(
      tap((res: PermissionResponse) => {
        if (res?.permissions) {
          this.permissions = res.permissions;
          this.permissions$.next(res.permissions);
          localStorage.setItem('userPermissions', JSON.stringify(res.permissions));
        }
      }),
      catchError((error) => {
        this.loggingService.error('Error loading permissions:', error);
        this.permissions = [];
        this.permissions$.next([]);
        return of(null);
      })
    );
  }


  /** 🟢 Get permissions as observable (for components to subscribe) */
  getPermissions$(): Observable<UserPermission[]> {
    return this.permissions$.asObservable();
  }

  /** 🟢 Load from local cache (faster than API) */
  loadFromCache(): void {
    const cached = localStorage.getItem('userPermissions');
    if (cached) {
      this.permissions = JSON.parse(cached);
      this.permissions$.next(this.permissions);
    }
  }

  /** 🟢 Check if user has specific permission */
  hasPermission(featureKey: string, permissionKey?: string): boolean {
    const feature = this.permissions.find((p) => p.featureKey === featureKey);
    if (!feature) return false;

    // fullAccess grants everything
    if (feature.permissionKeys.includes('fullAccess')) return true;

    // Check for specific permission
    return permissionKey ? feature.permissionKeys.includes(permissionKey) : true;
  }

  /** 🟢 Refresh permissions manually (e.g., after backend change) */
  refreshPermissions(): void {
    this.loadPermissions().subscribe();
  }

  /** 🟢 Clear permissions (on logout or session clear) */
  clearPermissions(): void {
    this.permissions = [];
    this.permissions$.next([]);
    localStorage.removeItem('userPermissions');
  }
  getFirstFeatureRoute(): Observable<string | null> {
    return this.baseService.getFeatureOptions().pipe(
      map((res: any) => {
        if (res.data?.features?.length) {
          const transformed = this.transformApiResponse(res.data.features);
          const firstParent = transformed[0];
          const firstChild = firstParent?.children?.[0];
          return firstChild?.route || null;
        }
        return null;
      }),
      catchError((err) => {
        this.loggingService.error('Error fetching features:', err);
        return of(null);
      })
    );
  }

  private transformApiResponse(features: any[]): any[] {
    // same logic as your sidenav transformApiResponse
    return features.map(f => ({
      id: f.id,
      navCap: f.navCap,
      expanded: false,
      children: f.children || []
    }));
  }

}
